import {
  type BvPincodeValidationResult,
  isValidUsZip,
  sanitizeBvPrice,
} from 'utils/bloodVisionUpsell';
import { BvUpsellCard } from 'content/products/bloodVisionUpsell';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';

type BvSnapPrice = Pick<
  BvPincodeValidationResult,
  'isServiceable' | 'resolvedPrice'
> | null | undefined;

export function computeBvDisplayPricePersistentSheet(
  hasResolvedBackendPrice: boolean,
  resolvedPrice: number | undefined,
  sharedSnap: BvSnapPrice,
  cardFallback: number | undefined,
): number | undefined {
  if (hasResolvedBackendPrice && typeof resolvedPrice === 'number') {
    return resolvedPrice;
  }
  if (
    sharedSnap?.isServiceable &&
    typeof sharedSnap.resolvedPrice === 'number'
  ) {
    return sharedSnap.resolvedPrice;
  }
  return sanitizeBvPrice(cardFallback);
}

export function computeBvDisplayPriceCollapsibleSheet(
  sheetOpen: boolean,
  resolvedPrice: number | undefined,
  sharedSnap: BvSnapPrice,
  cardFallback: number | undefined,
): number | undefined {
  if (sheetOpen) {
    return sanitizeBvPrice(resolvedPrice) ?? sanitizeBvPrice(cardFallback);
  }
  if (
    sharedSnap?.isServiceable &&
    typeof sharedSnap.resolvedPrice === 'number'
  ) {
    return sharedSnap.resolvedPrice;
  }
  return sanitizeBvPrice(cardFallback);
}

export function getBvShopifyVariantIdForAtc(
  store: ShopifyStore | null,
  card: BvUpsellCard,
  resolvedShopifyVariantId: string | undefined,
  sharedSnap: Pick<
    BvPincodeValidationResult,
    'resolvedShopifyVariantId'
  > | null | undefined,
): string {
  if (store !== ShopifyStore.US) {
    return card.shopifyVariantId;
  }
  return (
    resolvedShopifyVariantId ??
    sharedSnap?.resolvedShopifyVariantId ??
    card.shopifyVariantId
  );
}

type BvResolvedPricingContext =
  | { kind: 'modal' }
  | { kind: 'collapsible_card'; sheetOpen: boolean };

export function getBvHasResolvedBackendPriceCombined(
  store: ShopifyStore | null,
  hasResolvedBackendPrice: boolean,
  sharedSnap: Pick<
    BvPincodeValidationResult,
    'resolvedUsPlanDisplay' | 'resolvedPrice'
  > | null | undefined,
  context: BvResolvedPricingContext,
): boolean {
  const isUS = store === ShopifyStore.US;
  const snapOk =
    !!sharedSnap?.resolvedUsPlanDisplay &&
    typeof sharedSnap.resolvedPrice === 'number';

  if (context.kind === 'modal') {
    return isUS && (hasResolvedBackendPrice || snapOk);
  }

  if (!isUS) {
    return hasResolvedBackendPrice;
  }
  if (!context.sheetOpen) {
    return snapOk;
  }
  return hasResolvedBackendPrice || snapOk;
}

/**
 * Use shared US ZIP pricing when the sheet/modal is closed, or when open input
 * matches the committed session ZIP (avoids stale ATC while editing partial ZIP).
 */
export function shouldUseBvUsSharedSnapshot(
  store: ShopifyStore | null,
  pincode: string,
  committedZip: string | null,
  options?: { sheetOpen?: boolean },
): boolean {
  if (store !== ShopifyStore.US || !committedZip) return false;
  if (!options?.sheetOpen) return true;
  const normalized = pincode.trim();
  return isValidUsZip(normalized) && normalized === committedZip;
}

export function getBvCanAddToCartEffective(canAddToCart: boolean): boolean {
  return canAddToCart;
}

type BvAtcControlInput = {
  store: ShopifyStore | null;
  isBlockedBecauseInCart: boolean;
  canAddToCartEffective: boolean;
  isCheckingPincode: boolean;
  isAdding: boolean;
};

const isBvAtcAlwaysActiveStore = (store: ShopifyStore | null) =>
  store === ShopifyStore.AE || store === ShopifyStore.IN;

/** AE/IN keep ATC enabled while validation runs; US gates on serviceability + ZIP. */
export function getBvDisableAddToCart({
  store,
  isBlockedBecauseInCart,
  canAddToCartEffective,
  isCheckingPincode,
  isAdding,
}: BvAtcControlInput): boolean {
  if (isBlockedBecauseInCart || isAdding) return true;
  if (isBvAtcAlwaysActiveStore(store)) return false;
  return !canAddToCartEffective || isCheckingPincode;
}

export function getBvIsAddButtonActive({
  store,
  isBlockedBecauseInCart,
  canAddToCartEffective,
  isAdding,
}: Pick<
  BvAtcControlInput,
  'store' | 'isBlockedBecauseInCart' | 'canAddToCartEffective' | 'isAdding'
>): boolean {
  if (isBlockedBecauseInCart || isAdding) return false;
  if (isBvAtcAlwaysActiveStore(store)) return true;
  return canAddToCartEffective;
}

export function formatBvAtcPriceLabel(
  isUSStore: boolean,
  hasResolvedBackendPriceCombined: boolean,
  displayPriceLabel: string | null,
): string {
  const fallback = displayPriceLabel ?? 'price at checkout';
  if (!isUSStore) {
    return `Add to cart for ${fallback}`;
  }
  return hasResolvedBackendPriceCombined
    ? `Add to cart for ${fallback}`
    : `Starts from ${fallback}`;
}

export function getBvShowStartingAtRow(
  isUSStore: boolean,
  displayPrice: number | undefined,
  displayPriceLabel: string | null,
): boolean {
  return isUSStore && typeof displayPrice === 'number' && !!displayPriceLabel;
}
